<template>
  <div>
    <BackButton to="/settings" />
    <Toast />

    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-bold">Gerenciamento de Usuários do Sistema</h2>
      <Button v-if="!authStore.isCialLogin" label="Adicionar Usuário" icon="material-icons:add" severity="primary" @click="openModal">
        <template #icon>
          <span class="material-icons mr-1">add</span>
        </template>
      </Button>
    </div>

    <SystemAdminUserFilters
      :filters="store.filters"
      :accounts="accountStore.accounts"
      :loading="store.isLoading"
      @update:filters="store.setFilters"
      @clear="store.clearFilters"
    />

    <DataTable
      :value="store.users"
      stripedRows
      paginator
      lazy
      :rows="store.pageSize"
      :first="(store.page - 1) * store.pageSize"
      :totalRecords="store.totalUsers"
      :loading="store.isLoading"
      :sort-field="store.sortField"
      :sort-order="store.sortOrder === 'asc' ? 1 : -1"
      currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
      class="p-fluid shadow-sm rounded-lg border border-surface-200"
      @page="onUsersPage"
      @sort="onUsersSort"
    >
      <Column field="first_name" header="Nome" sortable />
      <Column field="last_name" header="Sobrenome" sortable />
      <Column field="email" header="Email" sortable />
      <Column field="is_system_admin" header="Admin Sistema" sortable class="w-36 text-center">
        <template #body="{ data }">
          <div class="flex justify-center">
            <span v-if="data.is_system_admin" class="px-2 py-1 rounded bg-purple-50 text-purple-700 text-[10px] font-bold border border-purple-100 uppercase tracking-wider flex items-center gap-1">
              <span class="material-icons text-[12px]">settings</span>
              Administrador
            </span>
            <span v-else class="text-xs text-surface-300">Não</span>
          </div>
        </template>
      </Column>
      <Column field="inserted_at" header="Criado em" sortable class="w-32">
        <template #body="{ data }">
          <span class="text-xs text-gray-600">
            {{ formatDate(data.inserted_at) }}
          </span>
        </template>
      </Column>
      <Column field="last_login_at" header="Último Login" sortable class="w-32">
        <template #body="{ data }">
          <span class="text-xs text-gray-600">
            {{ data.last_login_at ? formatDate(data.last_login_at) : 'Nunca' }}
          </span>
        </template>
      </Column>
      <Column header="Vínculos (Contas, Papéis e Permissões)">
        <template #body="{ data }">
          <div v-if="data.accounts.length === 0" class="flex items-center gap-2 text-orange-500 py-2">
            <span class="text-sm italic font-medium">Usuário sem vínculos ativos</span>
            <span class="material-icons text-sm">warning</span>
          </div>
          <div v-else class="flex flex-col gap-2 py-1">
            <div
              v-for="account in data.accounts"
              :key="account.id"
              class="flex items-center gap-3 p-2 rounded border border-surface-100 bg-surface-50/50"
            >
              <div class="flex-1 min-w-[120px]">
                <div class="text-xs font-bold text-surface-700 uppercase tracking-tight">{{ account.name }}</div>
              </div>

              <div class="flex items-center gap-2">
                <span v-if="account.role" class="px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-bold border border-blue-200">
                  {{ account.role.name }}
                </span>
                <span v-else class="text-[10px] text-surface-400 italic">Sem papel</span>

                <span v-if="account.is_admin" class="px-2 py-0.5 rounded bg-green-100 text-green-700 text-[10px] font-bold border border-green-200 flex items-center gap-1">
                  <span class="material-icons text-[10px]">verified_user</span>
                  ADMIN CONTA
                </span>
              </div>
            </div>
          </div>
        </template>
      </Column>
      <Column header="Ações" class="w-32">
        <template #body="{ data }">
          <div class="flex gap-1">
            <BaseButton icon="edit" text rounded @click="openEditModal(data)" title="Editar usuário" />
            <BaseButton v-if="!authStore.isCialLogin" icon="lock_reset" text rounded @click="openAdminChangePasswordModal(data)" title="Redefinir senha" />
            <BaseButton v-if="!authStore.isCialLogin" icon="delete" text rounded severity="danger" @click="openDeleteUserModal(data)" title="Excluir usuário" />
          </div>
        </template>
      </Column>
    </DataTable>

    <ConfirmDialog :draggable="false" />

    <ChangePasswordDialog
      v-if="!authStore.isCialLogin"
      v-model:visible="isAdminChangePasswordModalOpen"
      :userId="selectedUser?.id"
      :userName="selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''"
      isAdminMode
    />

    <!-- Create User Dialog -->
    <Dialog
      v-model:visible="isModalOpen"
      header="Adicionar Usuário"
      :modal="true"
      class="w-full max-w-3xl"
      :draggable="false"
    >
      <div class="flex flex-col gap-4 p-4 max-h-[75vh] overflow-y-auto">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex flex-col">
            <label for="first_name" class="text-xs font-semibold text-gray-500 mb-1">Nome <span class="text-red-500">*</span></label>
            <InputText id="first_name" v-model.trim="userForm.first_name" required autofocus :class="{ 'p-invalid': submitted && !userForm.first_name.trim() }" />
            <small v-if="submitted && !userForm.first_name.trim()" class="p-error">Nome é obrigatório</small>
          </div>
          <div class="flex flex-col">
            <label for="last_name" class="text-xs font-semibold text-gray-500 mb-1">Sobrenome <span class="text-red-500">*</span></label>
            <InputText id="last_name" v-model.trim="userForm.last_name" required :class="{ 'p-invalid': submitted && !userForm.last_name.trim() }" />
            <small v-if="submitted && !userForm.last_name.trim()" class="p-error">Sobrenome é obrigatório</small>
          </div>
        </div>

        <div class="flex flex-col">
          <label for="email" class="text-xs font-semibold text-gray-500 mb-1">Email <span class="text-red-500">*</span></label>
          <InputText id="email" v-model.trim="userForm.email" required type="email" :class="{ 'p-invalid': submitted && !userForm.email.trim() }" />
          <small v-if="submitted && !userForm.email.trim()" class="p-error">Email é obrigatório</small>
        </div>

        <div class="flex flex-col">
          <label for="password" class="text-xs font-semibold text-gray-500 mb-1">Senha <span class="text-red-500">*</span></label>
          <Password id="password" v-model="userForm.password" required :toggleMask="true" class="w-full" :class="{ 'p-invalid': submitted && !userForm.password }" />
          <small v-if="submitted && !userForm.password" class="p-error">Senha é obrigatória</small>
        </div>

        <div class="border-t border-surface-200 mt-2 pt-4">
          <UserMembershipManager
            v-model="membershipsEdit"
            :available-accounts="availableAccountsToAdd"
            :roles-by-account-id="rolesByAccountId"
            :description="!authStore.isCialLogin
              ? 'Alterações em contas (incluir, remover, papéis) só são aplicadas ao clicar em Atualizar.'
              : 'Altere apenas se o usuário é administrador nas contas em que já está vinculado.'"
            :disabled="isSaving"
            :allow-remove-memberships="!authStore.isCialLogin"
            :show-add-account="!authStore.isCialLogin"
            @add-account="onAddAccountPicked"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" @click="closeModal" class="p-button-text" />
        <Button label="Criar Usuário" @click="saveUser" :disabled="!isFormValid || isSaving" :loading="isSaving" />
      </template>
    </Dialog>

    <!-- Edit User Dialog -->
    <Dialog
      v-model:visible="isEditModalOpen"
      header="Editar Usuário"
      :modal="true"
      class="w-full max-w-3xl"
      :draggable="false"
    >
      <div class="flex flex-col gap-4 p-4 max-h-[75vh] overflow-y-auto">
        <div v-if="authStore.isCialLogin" class="flex flex-col gap-1">
          <div class="grid grid-cols-[8rem_1fr] items-baseline gap-3">
            <span class="text-sm font-semibold text-surface-500">Nome</span>
            <span class="text-base text-surface-900">{{ editUserForm.first_name || 'Não informado' }}</span>
          </div>
          <div class="grid grid-cols-[8rem_1fr] items-baseline gap-3">
            <span class="text-sm font-semibold text-surface-500">Sobrenome</span>
            <span class="text-base text-surface-900">{{ editUserForm.last_name || 'Não informado' }}</span>
          </div>
        </div>
        <template v-else>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="flex flex-col">
              <label for="edit_first_name" class="text-xs font-semibold text-gray-500 mb-1">Nome <span class="text-red-500">*</span></label>
              <InputText id="edit_first_name" v-model.trim="editUserForm.first_name" required autofocus :disabled="isSaving" :class="{ 'p-invalid': editSubmitted && !editUserForm.first_name.trim() }" />
              <small v-if="editSubmitted && !editUserForm.first_name.trim()" class="p-error">Nome é obrigatório</small>
            </div>
            <div class="flex flex-col">
              <label for="edit_last_name" class="text-xs font-semibold text-gray-500 mb-1">Sobrenome <span class="text-red-500">*</span></label>
              <InputText id="edit_last_name" v-model.trim="editUserForm.last_name" required :disabled="isSaving" :class="{ 'p-invalid': editSubmitted && !editUserForm.last_name.trim() }" />
              <small v-if="editSubmitted && !editUserForm.last_name.trim()" class="p-error">Sobrenome é obrigatório</small>
            </div>
          </div>
        </template>

        <div class="border-t border-surface-200 mt-2 pt-4">
          <UserMembershipManager
            v-model="membershipsEdit"
            :available-accounts="availableAccountsToAdd"
            :roles-by-account-id="rolesByAccountId"
            :description="!authStore.isCialLogin
              ? 'Alterações em contas (incluir, remover, papéis) só são aplicadas ao clicar em Atualizar.'
              : 'Altere apenas se o usuário é administrador nas contas em que já está vinculado.'"
            :disabled="isSaving"
            :allow-remove-memberships="!authStore.isCialLogin"
            :show-add-account="!authStore.isCialLogin"
            @add-account="onAddAccountPicked"
          />
        </div>
      </div>
      <template #footer>
        <Button label="Cancelar" @click="closeEditModal" class="p-button-text" :disabled="isSaving" />
        <Button label="Atualizar" @click="saveEditUser" :loading="isSaving" :disabled="isSaving" />
      </template>
    </Dialog>

    <UserDeleteDialog
      v-model:visible="isDeleteUserModalOpen"
      :user="selectedUser"
      :loading="isDeleting"
      @confirm="handleDeleteUser"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, reactive, computed } from 'vue';
import { isSystemAdminUserSortField, useSystemAdminUserStore } from '@/stores/systemAdminUserStore';
import { useSystemAdminAccountStore } from '@/stores/systemAdminAccountStore';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'primevue/usetoast';
import type { SystemUser, SystemAdminUserCreate } from '@/types/user';
import systemAdminUsersApi from '@/api/modules/systemAdminUsers';

// PrimeVue Components
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import ConfirmDialog from 'primevue/confirmdialog';

// Base Components
import BackButton from '@/components/base/BackButton.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import ChangePasswordDialog from '@/components/auth/ChangePasswordDialog.vue';
import { formatDate } from '@/utils/formatters';

// Sub-components
import UserMembershipManager, { type MembershipRow } from './components/UserMembershipManager.vue';
import UserDeleteDialog from './components/UserDeleteDialog.vue';
import SystemAdminUserFilters from './components/SystemAdminUserFilters.vue';

const store = useSystemAdminUserStore();
const accountStore = useSystemAdminAccountStore();
const authStore = useAuthStore();
const toast = useToast();

// State
const isModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isDeleteUserModalOpen = ref(false);
const isAdminChangePasswordModalOpen = ref(false);
const submitted = ref(false);
const editSubmitted = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);

const selectedUser = ref<SystemUser | null>(null);
const membershipsEdit = ref<MembershipRow[]>([]);
const rolesByAccountId = ref<Record<string, any[]>>({});

const userForm = reactive<SystemAdminUserCreate>({
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  memberships: [],
});

const editUserForm = reactive({
  first_name: '',
  last_name: '',
});

// Computed
const isFormValid = computed(() => {
  return !!(
    userForm.first_name.trim() &&
    userForm.last_name.trim() &&
    userForm.email.trim() &&
    userForm.password &&
    membershipsEdit.value.length > 0
  );
});

const availableAccountsToAdd = computed(() => {
  const linked = new Set(membershipsEdit.value.map((m) => m.accountId));
  return accountStore.accounts.filter((a) => !linked.has(a.id));
});

// Lifecycle
onMounted(() => {
  store.fetchUsers();
  accountStore.fetchAccounts();
});

const onUsersPage = (event: { page: number; rows: number }) => {
  store.fetchUsers(event.page + 1, event.rows);
};

const onUsersSort = (event: { sortField?: string | ((item: any) => string); sortOrder?: number | null }) => {
  if (typeof event.sortField !== 'string') return;
  if (!isSystemAdminUserSortField(event.sortField)) return;

  store.setSort(event.sortField, event.sortOrder === 1 ? 'asc' : 'desc');
};

// Helpers
const syncMembershipsFromUser = (user: SystemUser) => {
  membershipsEdit.value = user.accounts.map((a) => ({
    accountId: a.id,
    accountName: a.name,
    is_admin: a.is_admin,
    role_id: a.role?.id ?? null,
  }));
};

const loadRolesForAccountIds = async (accountIds: string[]) => {
  const unique = [...new Set(accountIds)].filter(Boolean);
  if (unique.length === 0) return;

  const entries = await Promise.all(
    unique.map(async (id) => {
      if (rolesByAccountId.value[id]) return [id, rolesByAccountId.value[id]] as const;
      const roles = await systemAdminUsersApi.getRoles(id);
      return [id, roles] as const;
    })
  );
  rolesByAccountId.value = { ...rolesByAccountId.value, ...Object.fromEntries(entries) };
};

// Actions
const openModal = () => {
  if (authStore.isCialLogin) return;

  userForm.first_name = '';
  userForm.last_name = '';
  userForm.email = '';
  userForm.password = '';
  membershipsEdit.value = [];
  submitted.value = false;
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  submitted.value = false;
  membershipsEdit.value = [];
};

const saveUser = async () => {
  if (authStore.isCialLogin) return;

  submitted.value = true;
  if (!isFormValid.value) return;

  if (membershipsEdit.value.length === 0) {
    toast.add({ severity: 'warn', summary: 'Atenção', detail: 'Inclua pelo menos uma conta para o novo usuário.', life: 5000 });
    return;
  }

  const payload: SystemAdminUserCreate = {
    email: userForm.email.trim(),
    first_name: userForm.first_name.trim(),
    last_name: userForm.last_name.trim(),
    password: userForm.password,
    memberships: membershipsEdit.value.map((m) => ({
      account_id: m.accountId,
      is_admin: m.is_admin,
      role_id: m.role_id || undefined,
    })),
  };

  isSaving.value = true;
  try {
    await store.createUser(payload);
    toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Usuário criado com sucesso', life: 3000 });
    closeModal();
  } catch (error: any) {
    toast.add({ severity: 'error', summary: 'Erro', detail: error.message || 'Erro ao criar usuário', life: 3000 });
  } finally {
    isSaving.value = false;
  }
};

const openEditModal = async (user: SystemUser) => {
  selectedUser.value = user;
  editUserForm.first_name = user.first_name;
  editUserForm.last_name = user.last_name;
  syncMembershipsFromUser(user);
  await loadRolesForAccountIds(user.accounts.map((a) => a.id));
  editSubmitted.value = false;
  isEditModalOpen.value = true;
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  editSubmitted.value = false;
  selectedUser.value = null;
  membershipsEdit.value = [];
};

const onAddAccountPicked = async (accountId: string) => {
  const acc = accountStore.accounts.find((a) => a.id === accountId);
  if (!acc || membershipsEdit.value.some((m) => m.accountId === accountId)) return;

  await loadRolesForAccountIds([accountId]);
  membershipsEdit.value.push({
    accountId: acc.id,
    accountName: acc.name,
    is_admin: false,
    role_id: null,
  });
};

const saveEditUser = async () => {
  if (!selectedUser.value) return;
  editSubmitted.value = true;

  if (!editUserForm.first_name.trim() || !editUserForm.last_name.trim()) {
    toast.add({ severity: 'error', summary: 'Erro', detail: 'Por favor, preencha todos os campos obrigatórios', life: 3000 });
    return;
  }

  if (membershipsEdit.value.length === 0) {
    toast.add({ severity: 'warn', summary: 'Atenção', detail: 'O usuário deve ter pelo menos um vínculo ativo.', life: 5000 });
    return;
  }

  isSaving.value = true;
  try {
    await store.bulkUpdateUser(selectedUser.value.id, {
      first_name: authStore.isCialLogin ? selectedUser.value.first_name : editUserForm.first_name.trim(),
      last_name: authStore.isCialLogin ? selectedUser.value.last_name : editUserForm.last_name.trim(),
      memberships: membershipsEdit.value.map((m) => ({
        account_id: m.accountId,
        is_admin: m.is_admin,
        role_id: m.role_id || undefined,
      })),
    });

    toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Usuário atualizado com sucesso', life: 3000 });
    closeEditModal();
  } catch (error: any) {
    toast.add({ severity: 'error', summary: 'Erro', detail: error.message || 'Erro ao atualizar usuário', life: 3000 });
  } finally {
    isSaving.value = false;
  }
};

const openDeleteUserModal = (user: SystemUser) => {
  if (authStore.isCialLogin) return;

  selectedUser.value = user;
  isDeleteUserModalOpen.value = true;
};

const handleDeleteUser = async () => {
  if (!selectedUser.value) return;
  if (authStore.isCialLogin) return;

  isDeleting.value = true;
  try {
    await store.deleteUser(selectedUser.value.id);
    toast.add({ severity: 'success', summary: 'Sucesso', detail: 'Usuário excluído com sucesso', life: 3000 });
    isDeleteUserModalOpen.value = false;
    selectedUser.value = null;
  } catch (error: any) {
    toast.add({ severity: 'error', summary: 'Erro', detail: error.message || 'Erro ao excluir usuário', life: 3000 });
  } finally {
    isDeleting.value = false;
  }
};

const openAdminChangePasswordModal = (user: SystemUser) => {
  if (authStore.isCialLogin) return;

  selectedUser.value = user;
  isAdminChangePasswordModalOpen.value = true;
};

</script>
